import {useCallback, useState, useRef, useEffect, memo} from 'react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Badge} from '@common-web/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {
  X,
  FileText,
  Plus,
  CloudUpload,
  AlertCircle,
  CheckCircle,
  Upload,
  Loader2,
  Wand2,
  Info,
} from 'lucide-react';
import {FileForUpload} from '../types';
import {Label} from '@common-web/components/ui/label';
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILES_PER_UPLOAD,
} from '@client/pages/payroll/constants/fileProperties';
import {
  formatFilesForUpload,
  guessFileType,
  sortAndReassignFileIds,
} from '@client/pages/payroll/utils/fileUtils';
import {toast} from '@common-web/hooks/use-toast';

export const FileUploadDropzone = memo(
  ({
    files,
    onFilesChange,
    disabled = false,
    behavior = 'individual',
  }: {
    files: FileForUpload[];
    onFilesChange: (files: FileForUpload[]) => void;
    disabled?: boolean;
    // either allow all files to be uploaded at once or individually
    behavior?: 'all-at-once' | 'individual';
  }) => {
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAddFilesIndividual = useCallback(
      (selectedFiles: FileList | File[]) => {
        if (disabled) return;

        const newFiles = formatFilesForUpload(selectedFiles, 0);

        const validNewFiles = newFiles.filter((file) => {
          if (file.error) {
            toast({
              title: 'File Upload Error',
              description: `Error with file "${file.file.name}": ${file.error}`,
              variant: 'destructive',
            });
            return false;
          }
          return true;
        });

        if (validNewFiles.length === 0) return;

        const existingNames = new Set(files.map((f) => f.file.name.toLowerCase()));
        const uniqueNewFiles = validNewFiles.filter(
          (f) => !existingNames.has(f.file.name.toLowerCase()),
        );

        if (uniqueNewFiles.length < validNewFiles.length) {
          toast({
            title: 'Duplicate Files Skipped',
            description: 'Some files were skipped because they have already been added.',
          });
        }

        const availableSlots = MAX_FILES_PER_UPLOAD - files.length;
        if (availableSlots <= 0) {
          toast({
            title: 'File Upload Limit Reached',
            description: `You have reached the maximum of ${MAX_FILES_PER_UPLOAD} files.`,
            variant: 'destructive',
          });
          return;
        }

        const filesToAdd = uniqueNewFiles.slice(0, availableSlots);
        if (filesToAdd.length < uniqueNewFiles.length) {
          toast({
            title: 'File Limit Reached',
            description: `Only ${filesToAdd.length} file(s) were added to stay within the limit of ${MAX_FILES_PER_UPLOAD}.`,
          });
        }

        if (filesToAdd.length > 0) {
          const combinedFiles = [...files, ...filesToAdd];
          const sortedFiles = sortAndReassignFileIds(combinedFiles);
          onFilesChange(sortedFiles);
        }
      },
      [files, onFilesChange, disabled],
    );

    const handleFilesAllAtOnce = useCallback(
      (selectedFiles: FileList | File[]) => {
        if (disabled) return;

        const updatedFiles = formatFilesForUpload(selectedFiles, 0);

        if (updatedFiles.length > MAX_FILES_PER_UPLOAD) {
          toast({
            title: 'File Upload Limit Exceeded',
            description: `You can upload a maximum of ${MAX_FILES_PER_UPLOAD} files at a time.`,
            variant: 'destructive',
          });
          return;
        }

        for (const file of updatedFiles) {
          if (file.error) {
            toast({
              title: 'File Upload Error',
              description: `Error with file "${file.file.name}": ${file.error}`,
              variant: 'destructive',
            });
            return;
          }
        }

        onFilesChange(updatedFiles);
      },
      [onFilesChange, disabled],
    );

    const handleFilesSelected = useCallback(
      (selectedFiles: FileList | File[]) => {
        if (behavior === 'all-at-once') {
          handleFilesAllAtOnce(selectedFiles);
        } else {
          handleAddFilesIndividual(selectedFiles);
        }
      },
      [behavior, handleFilesAllAtOnce, handleAddFilesIndividual],
    );

    const removeFile = (index: number) => {
      if (disabled) return;
      const newFiles = [...files];
      newFiles.splice(index, 1);
      const sortedFiles = sortAndReassignFileIds(newFiles);
      onFilesChange(sortedFiles);
    };

    const updateFile = (index: number, updates: Partial<FileForUpload>) => {
      if (disabled) return;
      const newFiles = [...files];
      newFiles[index] = {...newFiles[index], ...updates};
      onFilesChange(newFiles);
    };

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && (behavior === 'all-at-once' || files.length < MAX_FILES_PER_UPLOAD)) {
        setDragActive(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragActive(false);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (disabled || !e.dataTransfer.files) return;
      handleFilesSelected(e.dataTransfer.files);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFilesSelected(e.target.files);
        e.target.value = '';
      }
    };

    const hasErrors = files.some((f) => f.error);
    const canAddMore = behavior === 'all-at-once' || files.length < MAX_FILES_PER_UPLOAD;

    const getMainText = () => {
      if (dragActive && !disabled) return 'Drop files here';
      if (disabled) return 'Upload disabled';
      if (behavior === 'all-at-once') {
        return files.length > 0 ? 'Drop files to replace' : '';
      }
      if (!canAddMore) return `Maximum ${MAX_FILES_PER_UPLOAD} files reached`;
      return '';
    };

    const getSubText = () => {
      if (disabled) return 'File upload is currently disabled';
      if (behavior === 'all-at-once') {
        return files.length > 0
          ? 'Drag and drop to replace all files, or click to browse'
          : 'Drag and drop your CSV or Excel files here, or click to browse';
      }
      if (!canAddMore) return 'Remove some files to add more';
      return 'Drag and drop your CSV or Excel files here, or click to browse';
    };

    const getButtonText = () => {
      if (behavior === 'all-at-once') {
        return files.length > 0 ? 'Replace Files' : 'Choose Files';
      }
      return files.length > 0 ? 'Add More Files' : 'Choose Files';
    };

    return (
      <div className='space-y-6'>
        <Card className={disabled ? 'opacity-50' : ''}>
          <CardContent className='p-0'>
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                disabled
                  ? 'cursor-not-allowed bg-gray-50'
                  : 'cursor-pointer group hover:border-internal-brand-blue hover:bg-gray-50'
              } ${
                dragActive && !disabled
                  ? 'border-internal-brand-blue bg-internal-brand-blue/5 scale-[1.02]'
                  : 'border-gray-300'
              } overflow-hidden`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !disabled && canAddMore && fileInputRef.current?.click()}
            >
              <div className='absolute inset-0 opacity-5'>
                <div
                  className='absolute inset-0'
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234686ba' fill-opacity='0.4'%3E%3Cpath d='M20 20.5V18H18v2.5h-2.5V22H18v2.5h2v-2.5H22.5V20.5H20z'/%3E%3C/g%3E%3C/svg%3E")`,
                  }}
                />
              </div>
              <div className='relative z-10'>
                <div
                  className={`mx-auto mb-4 rounded-full h-20 w-20 flex items-center justify-center transition-colors ${
                    dragActive && !disabled
                      ? 'bg-internal-brand-blue text-white'
                      : disabled
                        ? 'bg-gray-200 text-gray-400'
                        : 'bg-internal-brand-blue/10 text-internal-brand-blue group-hover:bg-internal-brand-blue group-hover:text-white'
                  }`}
                >
                  <CloudUpload className='h-8 w-8' />
                </div>
                <div className='space-y-2'>
                  <div className='text-xl font-semibold text-internal-brand-navy'>
                    {getMainText()}
                  </div>
                  <div className='text-internal-brand-gray-dark'>{getSubText()}</div>
                  <div className='flex items-center justify-center gap-2 text-sm text-internal-brand-gray-dark'>
                    {ACCEPTED_FILE_EXTENSIONS.map((type) => (
                      <Badge key={type} variant='outline' className='text-xs'>
                        {type.replace('.', '').toUpperCase()}
                      </Badge>
                    ))}
                    <span>•</span>
                    <span>Max {MAX_FILES_PER_UPLOAD} files</span>
                    <span>•</span>
                    <span>50MB each</span>
                  </div>
                </div>
                {canAddMore && !disabled && (
                  <Button
                    variant='outline'
                    className='mt-4 border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:border-internal-brand-blue hover:text-white'
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    <Plus className='h-4 w-4 mr-2' />
                    {getButtonText()}
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type='file'
                accept={ACCEPTED_FILE_TYPES.join(',')}
                multiple
                className='hidden'
                onChange={handleFileInputChange}
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
        {files.length > 0 && (
          <Card className='border-gray-200 shadow-sm'>
            <CardContent className='p-0'>
              <div className='p-4 border-b bg-gray-50'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-lg font-semibold text-internal-brand-navy'>Selected Files</h3>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline' className='text-internal-brand-blue bg-white'>
                      {files.length} file{files.length !== 1 ? 's' : ''}
                    </Badge>
                    {hasErrors && (
                      <Badge className='bg-red-600 text-white border-0 font-semibold'>
                        <AlertCircle className='h-3 w-3 mr-1' />
                        Errors
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow className='bg-gray-50'>
                      <TableHead className='font-semibold'>File</TableHead>
                      <TableHead className='font-semibold'>File ID</TableHead>
                      <TableHead className='font-semibold'>Delimiter</TableHead>
                      <TableHead className='font-semibold'>Status</TableHead>
                      <TableHead className='w-12'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.map((fileData, i) => (
                      <TableRow key={i} className='hover:bg-gray-50'>
                        <TableCell>
                          <div className='flex items-center gap-3'>
                            <div className='p-2 bg-internal-brand-blue/10 rounded-lg'>
                              <FileText className='h-4 w-4 text-internal-brand-blue' />
                            </div>
                            <div>
                              <div className='text-sm font-medium text-internal-brand-navy'>
                                {fileData.file.name}
                              </div>
                              <div className='text-xs text-internal-brand-gray'>
                                {Math.round(fileData.file.size / 1024)} KB •{' '}
                                {guessFileType(fileData.file.name)}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={fileData.fileId}
                            onChange={(e) => {
                              const newFileId = e.target.value.replace(/[^0-9a-zA-Z-_]/g, '');
                              updateFile(i, {fileId: newFileId});
                            }}
                            className='h-8 w-64 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                            placeholder='Enter file ID'
                            disabled={disabled}
                          />
                        </TableCell>
                        <TableCell>
                          {guessFileType(fileData.file.name) === 'CSV' ? (
                            <Select
                              value={fileData.delimiter}
                              onValueChange={(value: ',' | ';' | '\t') => {
                                updateFile(i, {delimiter: value});
                              }}
                              disabled={disabled}
                            >
                              <SelectTrigger className='h-8 w-32 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value=','>, (Comma)</SelectItem>
                                <SelectItem value=';'>; (Semicolon)</SelectItem>
                                <SelectItem value='\t'>Tab</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant='gray' className='text-xs'>
                              N/A
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {fileData.error ? (
                            <Badge className='bg-red-600 text-white border-0 font-semibold text-xs'>
                              <AlertCircle className='h-3 w-3 mr-1' />
                              Error
                            </Badge>
                          ) : (
                            <Badge className='bg-emerald text-white border-0 font-semibold text-xs'>
                              <CheckCircle className='h-3 w-3 mr-1' />
                              Ready
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className='text-right'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => removeFile(i)}
                            className='h-8 w-8 p-0 text-internal-brand-gray hover:text-red-600 hover:bg-red-50'
                            disabled={disabled}
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {hasErrors && (
                <div className='p-4 border-t bg-red-50'>
                  <div className='space-y-2'>
                    {files
                      .filter((f) => f.error)
                      .map((fileData, i) => (
                        <div key={i} className='flex items-start gap-2 text-sm text-red-800'>
                          <AlertCircle className='h-4 w-4 text-red-600 mt-0.5 shrink-0' />
                          <div>
                            <span className='font-medium'>{fileData.file.name}:</span>{' '}
                            {fileData.error}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  },
);

export const ColumnExtractUploader = memo(
  ({
    files,
    onFilesUpdated,
    onExtract,
    isExtracting,
    primaryFileId,
    onPrimaryFileIdChange,
    behavior = 'individual',
  }: {
    files: FileForUpload[];
    onFilesUpdated: (files: FileForUpload[]) => void;
    onExtract: () => void;
    isExtracting: boolean;
    primaryFileId?: string;
    onPrimaryFileIdChange?: (fileId: string | undefined) => void;
    // either allow all files to be uploaded at once or individually
    behavior?: 'all-at-once' | 'individual';
  }) => {
    const [dragActive, setDragActive] = useState(false);
    const [fileIdTipOpen, setFileIdTipOpen] = useState(true);
    const [fileIdDrafts, setFileIdDrafts] = useState<string[]>(() => files.map((f) => f.fileId));

    useEffect(() => {
      const currentIds = files.map((f) => f.fileId);
      if (
        fileIdDrafts.length !== currentIds.length ||
        fileIdDrafts.some((id, idx) => id !== currentIds[idx])
      ) {
        setFileIdDrafts(currentIds);
      }
    }, [files]);

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (behavior === 'individual' && files.length < MAX_FILES_PER_UPLOAD) {
        setDragActive(true);
      } else if (behavior === 'all-at-once') {
        setDragActive(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragActive(false);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFilesSelected(e.dataTransfer.files);
      }
    };

    const handleAddFilesIndividual = useCallback(
      (fileList: FileList | File[]) => {
        const newFiles = formatFilesForUpload(fileList, 0);

        const validNewFiles = newFiles.filter((file) => {
          if (file.error) {
            toast({
              title: 'File Upload Error',
              description: `Error with file "${file.file.name}": ${file.error}`,
              variant: 'destructive',
            });
            return false;
          }
          return true;
        });

        if (validNewFiles.length === 0) return;

        const existingNames = new Set(files.map((f) => f.file.name.toLowerCase()));
        const uniqueNewFiles = validNewFiles.filter(
          (f) => !existingNames.has(f.file.name.toLowerCase()),
        );

        if (uniqueNewFiles.length < validNewFiles.length) {
          toast({
            title: 'Duplicate Files Skipped',
            description: 'Some files were skipped because they have already been added.',
          });
        }

        const availableSlots = MAX_FILES_PER_UPLOAD - files.length;
        if (availableSlots <= 0) {
          toast({
            title: 'File Upload Limit Reached',
            description: `You have reached the maximum of ${MAX_FILES_PER_UPLOAD} files.`,
            variant: 'destructive',
          });
          return;
        }

        const filesToAdd = uniqueNewFiles.slice(0, availableSlots);
        if (filesToAdd.length < uniqueNewFiles.length) {
          toast({
            title: 'File Limit Reached',
            description: `Only ${filesToAdd.length} file(s) were added to stay within the limit of ${MAX_FILES_PER_UPLOAD}.`,
          });
        }

        if (filesToAdd.length > 0) {
          const combinedFiles = [...files, ...filesToAdd];
          const sortedFiles = sortAndReassignFileIds(combinedFiles);

          if (primaryFileId) {
            const previousPrimaryFile = files.find((f) => f.fileId === primaryFileId);
            if (previousPrimaryFile) {
              const newPrimaryFile = sortedFiles.find(
                (f) => f.file.name === previousPrimaryFile.file.name,
              );
              if (newPrimaryFile && newPrimaryFile.fileId !== primaryFileId) {
                onPrimaryFileIdChange?.(newPrimaryFile.fileId);
              }
            }
          }

          onFilesUpdated(sortedFiles);
        }
      },
      [files, onFilesUpdated, primaryFileId, onPrimaryFileIdChange],
    );

    const handleFilesAllAtOnce = useCallback(
      (fileList: FileList | File[]) => {
        const updatedFiles = formatFilesForUpload(fileList, 0);

        if (updatedFiles.length > MAX_FILES_PER_UPLOAD) {
          toast({
            title: 'File Upload Limit Exceeded',
            description: `You can upload a maximum of ${MAX_FILES_PER_UPLOAD} files at a time.`,
            variant: 'destructive',
          });
          return;
        }

        for (const file of updatedFiles) {
          if (file.error) {
            toast({
              title: 'File Upload Error',
              description: `Error with file "${file.file.name}": ${file.error}`,
              variant: 'destructive',
            });
            return;
          }
        }

        if (primaryFileId) {
          const primaryStillExists = updatedFiles.some((f) => f.fileId === primaryFileId);
          if (!primaryStillExists) {
            onPrimaryFileIdChange?.(undefined);
          }
        }

        onFilesUpdated(updatedFiles);
      },
      [onFilesUpdated, primaryFileId, onPrimaryFileIdChange],
    );

    const handleFilesSelected = useCallback(
      (fileList: FileList | File[]) => {
        if (behavior === 'all-at-once') {
          handleFilesAllAtOnce(fileList);
        } else {
          handleAddFilesIndividual(fileList);
        }
      },
      [behavior, handleFilesAllAtOnce, handleAddFilesIndividual],
    );

    const removeFile = useCallback(
      (index: number) => {
        const newFiles = [...files];
        const removedFile = newFiles[index];
        newFiles.splice(index, 1);

        const sortedFiles = sortAndReassignFileIds(newFiles);

        if (removedFile && removedFile.fileId === primaryFileId) {
          onPrimaryFileIdChange?.(undefined);
        } else if (primaryFileId) {
          const previousPrimaryFile = files.find((f) => f.fileId === primaryFileId);
          if (previousPrimaryFile) {
            const newPrimaryFile = sortedFiles.find(
              (f) => f.file.name === previousPrimaryFile.file.name,
            );
            if (newPrimaryFile && newPrimaryFile.fileId !== primaryFileId) {
              onPrimaryFileIdChange?.(newPrimaryFile.fileId);
            }
          }
        }

        onFilesUpdated(sortedFiles);
      },
      [files, onFilesUpdated, primaryFileId, onPrimaryFileIdChange],
    );

    const canAddMore = behavior === 'all-at-once' || files.length < MAX_FILES_PER_UPLOAD;

    const getUploadButtonText = () => {
      if (behavior === 'all-at-once') {
        return files.length === 0 ? 'Choose Files' : 'Replace Files';
      }
      if (!canAddMore) {
        return `Maximum ${MAX_FILES_PER_UPLOAD} files reached`;
      }
      return files.length === 0 ? 'Choose Files' : 'Add More Files';
    };

    return (
      <div className='space-y-6'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4'>
          <div className='flex-1'>
            <div
              className='relative'
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div
                className={`bg-white flex items-center gap-3 p-3 border-2 border-dashed rounded-lg transition-colors ${
                  canAddMore
                    ? 'border-gray-300 hover:border-internal-brand-blue cursor-pointer group'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                } ${dragActive ? 'border-internal-brand-blue bg-internal-brand-blue/5' : ''}`}
              >
                <div
                  className={`p-2 rounded-lg transition-colors ${
                    canAddMore
                      ? 'bg-internal-brand-blue/10 group-hover:bg-internal-brand-blue group-hover:text-white'
                      : 'bg-gray-200'
                  }`}
                >
                  <Upload
                    className={`h-5 w-5 ${canAddMore ? 'text-internal-brand-blue group-hover:text-white' : 'text-gray-400'}`}
                  />
                </div>
                <div className='flex-1'>
                  <div
                    className={`text-sm font-medium ${canAddMore ? 'text-internal-brand-navy' : 'text-gray-400'}`}
                  >
                    {getUploadButtonText()}
                  </div>
                  <div className='text-xs text-internal-brand-gray'>
                    {ACCEPTED_FILE_EXTENSIONS.join(', ').replace(/\./g, '').toUpperCase()} files
                    supported
                    {behavior === 'all-at-once' &&
                      files.length > 0 &&
                      ' • New selection replaces all'}
                  </div>
                </div>
              </div>
              <input
                type='file'
                accept={ACCEPTED_FILE_TYPES.join(',')}
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFilesSelected(e.target.files);
                    e.target.value = '';
                  }
                }}
                disabled={!canAddMore}
                className={`absolute inset-0 w-full h-full opacity-0 ${canAddMore ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              />
            </div>
          </div>
          <div className='flex items-center gap-3'>
            {files.length > 0 && (
              <Button
                onClick={onExtract}
                disabled={isExtracting}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white font-semibold'
              >
                {isExtracting ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <Wand2 className='h-4 w-4 mr-2' />
                )}
                Extract Columns
              </Button>
            )}
          </div>
        </div>
        {files.length > 0 && (
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <h4 className='text-sm font-semibold text-internal-brand-navy'>File Configuration</h4>
              <Badge variant='outline' className='text-internal-brand-blue bg-white'>
                {files.length} file{files.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            {fileIdTipOpen && (
              <div className='p-3 bg-white border border-gray-300 rounded-lg'>
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex items-start gap-2'>
                    <Info className='h-4 w-4 text-internal-brand-blue mt-0.5 shrink-0' />
                    <div className='text-sm text-gray-600'>
                      <span className='font-medium text-gray-600'>Tip:</span> Use the file ID to
                      give the file a meaningful name for mapping purposes, like "employees" or
                      "deductions".
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => setFileIdTipOpen(false)}
                    className='h-6 w-6 p-0 text-internal-brand-gray hover:text-internal-brand-navy'
                  >
                    <X className='h-3 w-3' />
                  </Button>
                </div>
              </div>
            )}
            <div className='space-y-3'>
              {files.map((f, i) => (
                <Card key={i} className='border-gray-200 shadow-sm'>
                  <CardContent className='p-4'>
                    <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4'>
                      <div className='flex items-center gap-3 flex-1'>
                        <div className='p-2 bg-internal-brand-blue/10 rounded-lg'>
                          <FileText className='h-4 w-4 text-internal-brand-blue' />
                        </div>
                        <div>
                          <div className='text-sm font-medium text-internal-brand-navy break-all'>
                            {f.file.name}
                          </div>
                          <div className='text-xs text-internal-brand-gray'>
                            {Math.round(f.file.size / 1024)} KB • {guessFileType(f.file.name)}
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col sm:flex-row items-start sm:items-center gap-3'>
                        {files.length > 1 && (
                          <div className='flex items-center gap-2'>
                            <Button
                              variant={f.fileId === primaryFileId ? 'default' : 'outline'}
                              size='sm'
                              className={
                                f.fileId === primaryFileId
                                  ? 'bg-emerald hover:bg-emerald/90 text-white border-emerald h-8 px-2'
                                  : 'h-8 border-gray-200 text-gray-500 px-2'
                              }
                              onClick={() =>
                                onPrimaryFileIdChange?.(
                                  f.fileId === primaryFileId ? undefined : f.fileId,
                                )
                              }
                            >
                              {f.fileId === primaryFileId ? (
                                <CheckCircle className='h-3 w-3' />
                              ) : null}
                              {f.fileId === primaryFileId ? 'Primary' : 'Set Primary'}
                            </Button>
                          </div>
                        )}
                        {guessFileType(f.file.name) === 'CSV' && (
                          <div className='flex items-center gap-2'>
                            <Label className='text-xs font-medium text-internal-brand-navy'>
                              Delimiter
                            </Label>
                            <Select
                              value={f.delimiter}
                              onValueChange={(v) => {
                                const next = [...files];
                                next[i] = {
                                  ...next[i],
                                  delimiter: v as Required<FileForUpload['delimiter']>,
                                };
                                onFilesUpdated(next);
                              }}
                            >
                              <SelectTrigger className='h-8 w-28 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value=','>, (Comma)</SelectItem>
                                <SelectItem value=';'>; (Semicolon)</SelectItem>
                                <SelectItem value='\t'>Tab</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className='flex items-center gap-2'>
                          <Label className='text-xs font-medium text-internal-brand-navy'>
                            File ID
                          </Label>
                          <Input
                            value={fileIdDrafts[i] ?? f.fileId}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9a-zA-Z-_]/g, '');
                              setFileIdDrafts((prev) => {
                                const copy = [...prev];
                                copy[i] = val;
                                return copy;
                              });
                            }}
                            onBlur={() => {
                              const draft = fileIdDrafts[i] ?? f.fileId;
                              if (draft !== f.fileId) {
                                if (f.fileId === primaryFileId) {
                                  onPrimaryFileIdChange?.(draft);
                                }
                                const next = [...files];
                                next[i] = {
                                  ...next[i],
                                  fileId: draft,
                                };
                                onFilesUpdated(next);
                              }
                            }}
                            className='h-8 w-32 border-gray-200 focus:border-internal-brand-blue focus:ring-internal-brand-blue'
                            placeholder='Enter ID'
                          />
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => removeFile(i)}
                          className='h-8 w-8 p-0 text-internal-brand-gray hover:text-red-600 hover:bg-red-50'
                        >
                          <X className='h-4 w-4' />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);
